//Vue imports
import { createRouter, createWebHistory } from "vue-router";

// Our imports
import { validate_route } from "@/utils/auth/auth.js";
import { GET_PROFILE_STATUS_URL } from "@/utils/constants/constants";
import FirstTimeLogin from "@/views/onboarding/FirstTimeLogin/FirstTimeLogin.vue";
import ForgotPassword from "@/views/onboarding/Forgot_password/ForgotPassword.vue";
import HandleSSOLogin from "@/views/onboarding/Handle_SSO_login/HandleSSOLogin.vue";
import UserLogin from "@/views/onboarding/Login/UserLogin.vue";
import OneTimePin from "@/views/onboarding/OneTimePin/OneTimePin.vue";
import RegistrationForm from "@/views/onboarding/RegistrationForm/RegistrationForm.vue";
import ResetPassword from "@/views/onboarding/Reset_password/ResetPassword.vue";
import ResetPasswordSent from "@/views/onboarding/ResetPasswordSent/ResetPasswordSent.vue";
import JobsAdvertised from "@/views/probaworkflows_base/entity/jobs_advertised/JobsAdvertised.vue";
import SignUp from "@/views/probaworkflows_base/entity/entity_onboarding/sign_up/SignUp.vue";
import EnquiryLandingPage from "@/views/probaworkflows_base/management/customer_management/enquiry_landing_page/EnquiryLandingPage.vue";
import ProspectiveApplicant from "@/views/probaworkflows_base/management/customer_management/prospective_applicant/ProspectiveApplicant.vue";
import axios from "axios";
import probaworkflows_routes from "./routes/probaworkflows";

// Get current domain from Django-injected variable
let hostname = window.location.host.split(':')[0]; // without port
let parts = hostname.split('.');
// Remove last part
parts.pop();

// If first part contains 'uat' (case-insensitive), remove it
if (parts[0] && ['uat', 'stage', 'demo'].includes(parts[0].toLowerCase())) {
  parts.shift();
}

const current_domain = parts.join('.');

// Map domains to routes
const domain_route_map = {
  "trustfirst.probaanalytics": probaworkflows_routes,
  "business.complyon": probaworkflows_routes,
  "complyon": probaworkflows_routes,
  "complyon.ai": probaworkflows_routes,
  "aicampus.complyon": probaworkflows_routes,
  "rematholdings.complyon": probaworkflows_routes,
  "gov.complyon": probaworkflows_routes,
  "vettedafrica.complyon": probaworkflows_routes,
  "probaworkflows": probaworkflows_routes,
  "bolide.complyon" : probaworkflows_routes,
  "x-verdict" : probaworkflows_routes,
  "ndabenitrust.complyon" : probaworkflows_routes,
};

// routes based on domain or fallback to probaworkflows
const routes = Reflect.get(domain_route_map, current_domain) || probaworkflows_routes;


const common_routes = [
  {
    path: "/",
    name: "jobs_advertised",
    component: JobsAdvertised,
    meta: { requires_auth: false, requires_otp: false },
  },
  {
    path: "/jobs/:slug",
    name: "job_advert_share",
    component: JobsAdvertised,
    meta: { requires_auth: false, requires_otp: false },
  },
  // Onboarding
  {
    path: "/login",
    name: "UserLogin",
    component: UserLogin,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for login
    beforeEnter: (to, from, next) => {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const redirect_reason = sessionStorage.getItem('auth_redirect_reason');

      // Only allow /login if explicitly triggered by navbar button
      if (redirect_reason === 'navbar_login') {
        sessionStorage.removeItem('auth_redirect_reason');
        return next();
      }

      // Allow /login when user is authenticated (e.g., logout flow)
      if (token) {
        return next();
      }

      // Block unintentional landing on /login and keep public landing page as default.
      console.warn('[ROUTER] Blocked unintended navigation to /login; redirecting to /');
      return next('/');
    },
  },
    {
    path: "/handle_sso_login/:token?/:user_id?/:user_role?/:is_aic_one_login?/:current_system?/",
    name: "HandleSSOLogin",
    component: HandleSSOLogin,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for login
    beforeEnter: (to, from, next) => {
      const token = to.params.token;
      const userId = to.params.user_id;

      if (token && userId) {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user_id', userId);
        next();
      } else {
        next('/login');
      }
    }

  },
  {
    path: "/first_time_login",
    name: "FirstTimeLogin",
    component: FirstTimeLogin,
    meta: { requires_otp: false }, // No OTP verification required for first time login
  },
  {
    path: "/one_time_pin",
    name: "OneTimePin",
    component: OneTimePin,
    meta: { requires_otp: false }, // No OTP verification required for one time pin
  },
  {
    path: "/reset_password/:encrypted_link",  // Updated to capture the encrypted link
    name: "ResetPassword",
    component: ResetPassword,
    meta: { requires_auth: false, requires_otp: false },
  },
  {
    path: "/forgot_password",
    name: "ForgotPassword",
    component: ForgotPassword,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for forgot password
  },
  {
    path: "/reset_password_sent",
    name: "ResetPasswordSent",
    component: ResetPasswordSent,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for reset password sent
  },

   // Entity Registering
  {
    path: "/registration",
    name: "registration",
    component: RegistrationForm,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for login
  },


  // Entity Onboarding
  {
    path: "/sign_up/:encrypted_form_id",
    name: "sign_up",
    component: SignUp,
  },

  // Global Components (outside of a project/company)

  {
    path: "/enquiry_landing_page",
    name: "enquiry_landing_page",
    component: EnquiryLandingPage,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for login
  },
  {
    path: "/prospective_applicant",
    name: "prospective_applicant",
    component: ProspectiveApplicant,
    meta: { requires_auth: false, requires_otp: false }, // No authentication required for login
  },




];

const router = createRouter({
  history: createWebHistory(),
  routes: [...common_routes, ...routes],
});
function is_internal() {
  return process.env.VUE_APP_IS_INTERNAL === 'true';
}

function get_aic_one_login_url() {
  return process.env.VUE_APP_AIC_ONE_LOGIN_URL || null;
}

router.beforeEach((to, from, next) => {
  console.log('[ROUTER] beforeEach:', { to_path: to.path, to_name: to.name, from_path: from.path, from_name: from.name });

  if (to.path === '/' || to.path === '') {
    console.log('[ROUTER] Root path — letting through to jobs_advertised');
    return next();
  }

  if (to.query.token) {
    const sso_token = to.query.token;
    localStorage.setItem('encrypted_token', sso_token);
    localStorage.setItem('token', sso_token);

    const clean_query = { ...to.query };
    delete clean_query.token;

    return next({
      path: to.path,
      query: clean_query,
      replace: true
    });
  }
  const token = localStorage.getItem('encrypted_token') || localStorage.getItem('token') || to.params.token;
  const is_public_route = to.matched.some(record => record.meta.requires_auth === false);

  // Redirect internal apps login page to aic one login
  if (is_internal() && !token && !is_public_route) {
    const url = get_aic_one_login_url();
    if (url) {
      const host = window.location.hostname.toLowerCase();

      const APP_MAP = [
        { match: 'probaworkflows', next: 'proba_workflow' },
        { match: 'complyon', next: 'complyon' },
        { match: 'x-verdict', next: 'xverdict' },
      ];

      const mapping = APP_MAP.find(r => host.includes(r.match));
      const next = mapping?.next || 'complyon';

      window.location.replace(`${url}?next=${next}`);
      return false;
    }
  }



  if (!validate_route(router, to)) return;

  if (current_domain === 'trustfirst' && to.name?.startsWith('other-')) {
    return next('/');
  }

  const role = sessionStorage.getItem('user_role');
  const otp_verified = sessionStorage.getItem('otp_verified');
  const user_id = sessionStorage.getItem('user_id');
  const token_from_storage = sessionStorage.getItem('token') || localStorage.getItem('token');
  const explicit_application_form_id = Number(
    to.query?.application_form_id || sessionStorage.getItem('application_form_id')
  ) || null;

  if (
    role === 'Entity'
    && otp_verified
    && token_from_storage
    && user_id
    && !is_public_route
    && (to.path === '/entity_onboarding_forms' || to.path === '/attribute_forms')
    && explicit_application_form_id
  ) {
    sessionStorage.setItem('application_form_id', String(explicit_application_form_id));
    sessionStorage.setItem('entity_applying', 'true');
    return next();
  }

  if (role === 'Entity' && otp_verified && token_from_storage && user_id && !is_public_route) {
    return axios.get(GET_PROFILE_STATUS_URL, {
      headers: {
        Authorization: `Token ${token_from_storage}`,
        'Content-Type': 'application/json'
      },
      params: { user_id }
    }).then((response) => {
      const profile_data = response.data?.data || {};
      const next_application_form_id = profile_data.next_application_form_id;

      if (next_application_form_id) {
        sessionStorage.setItem('application_form_id', String(next_application_form_id));
      } else {
        sessionStorage.removeItem('application_form_id');
      }

      const has_pending_form = profile_data.next_route === '/attribute_forms';
      const is_applying = sessionStorage.getItem('entity_applying') === 'true';

      if (
        (to.path === '/entity_onboarding_forms' || to.path === '/attribute_forms') && !is_applying && !has_pending_form
      ) {
        if (profile_data.closed_advert_form_skipped) {
          return next({
            path: '/view_all_user_forms',
            query: { tab: 'jobs', tracker: 'all_jobs' }
          });
        }
        return next('/view_all_user_forms');
      }

      if (has_pending_form && !is_applying) {
        sessionStorage.setItem('entity_applying', 'true');
      }

      return next();
    }).catch(() => {
      return next();
    });
  }

  next();

});

export function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(trimmedCookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export function set_auth(token, user_id) {
  sessionStorage.setItem('token', token);
  sessionStorage.setItem('user_id', user_id);
  sessionStorage.setItem('otp_required', '1');
  sessionStorage.setItem('otp_verified', '0');
}

export default router;
